'use client'

import { useEffect, useRef } from 'react'

/**
 * Interactive floating "skuf" props (PC, beer, chips) drawn on a <canvas>
 * behind the hero.
 * - calm idle drift + neighbour separation (keeps them spread out)
 * - cursor repulsion: items scatter away from the pointer (with a perpendicular
 *   swirl so they curve off rather than fleeing in a straight line)
 * - hold the mouse button: items are instead pulled into a vortex at the cursor
 *
 * Pure canvas + requestAnimationFrame — no dependencies. Honours
 * prefers-reduced-motion (renders a single static frame) and pauses the loop
 * while the hero is scrolled out of view.
 */

const SPRITES = ['/skuf/pc.png', '/skuf/soda.png', '/skuf/chips.png']

const FISH_SIZE = 36
const HALF = FISH_SIZE / 2

// density: one fish per N css-pixels², clamped to [MIN, MAX]
const AREA_PER_FISH = 9000
const MIN_FISH = 24
const MAX_FISH = 120

const SPEED = 2 // global tempo multiplier
const MAX_SPEED = 4 * SPEED
const FRICTION = 0.98
const WANDER = 0.04 * SPEED // idle steering jitter
const IDLE_CRUISE_MIN = 0.5 // px/frame — fish never drift slower than this when idle
const IDLE_CRUISE_MAX = 1.5

const REPEL_RADIUS = 180
const REPEL_FORCE = 5
const REPEL_SWIRL = 0.5

const ATTRACT_RADIUS = 600
const ATTRACT_FORCE = 8
const ATTRACT_SWIRL = 0.6

const SEPARATION_RADIUS = 34
const SEPARATION_FORCE = 0.3

const EDGE_MARGIN = 8
const GLOBAL_ALPHA = 0.85

interface Fish {
	x: number
	y: number
	vx: number
	vy: number
	sprite: number
	cruise: number // idle cruising speed (px/frame)
}

export default function FishBackground({
	className = '',
}: {
	className?: string
}) {
	const canvasRef = useRef<HTMLCanvasElement>(null)

	useEffect(() => {
		const canvas = canvasRef.current
		if (!canvas) return
		const ctx = canvas.getContext('2d', { alpha: true })
		if (!ctx) return

		const reduceMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches

		// fish must stay between the navbar and the footer (never under either).
		// Re-measured every frame this would force a layout read 60x/sec — during
		// mobile scroll (where the address bar keeps resizing the viewport) that's
		// enough to visibly stutter, so cache it for a few frames like obstacles below.
		const headerEl = document.getElementById('app-header')
		const footerEl = document.querySelector('footer')
		let cachedBounds = { top: 0, bottom: 0 }
		let boundsRefresh = 0
		const verticalBounds = () => {
			if (boundsRefresh <= 0) {
				let top = 0
				let bottom = height
				if (headerEl) {
					const hb = headerEl.getBoundingClientRect().bottom
					if (hb > top) top = hb
				}
				if (footerEl) {
					const ft = footerEl.getBoundingClientRect().top
					if (ft < bottom) bottom = ft
				}
				if (bottom < top + 40) bottom = top + 40
				cachedBounds = { top, bottom }
				boundsRefresh = 10
			}
			boundsRefresh--
			return cachedBounds
		}

		// --- sprites ---
		const images: (HTMLImageElement | null)[] = SPRITES.map(() => null)
		let loaded = 0
		SPRITES.forEach((src, i) => {
			const img = new Image()
			img.onload = () => {
				images[i] = img
				loaded++
			}
			img.onerror = () => {
				loaded++
			}
			img.src = src
		})

		let width = 0
		let height = 0
		const fish: Fish[] = []
		const mouse = { x: -9999, y: -9999, inside: false, down: false }

		// solid cards the items bounce off (DOM nodes marked with .skuf-obstacle).
		// rects are expanded by HALF so a sprite stops with its edge at the card.
		let obstacleEls: Element[] = []
		let obstacleRefresh = 0
		const obstacles: {
			left: number
			right: number
			top: number
			bottom: number
		}[] = []
		const collectObstacles = () => {
			if (obstacleRefresh <= 0) {
				obstacleEls = Array.from(document.querySelectorAll('.skuf-obstacle'))
				obstacleRefresh = 30
			}
			obstacleRefresh--
			obstacles.length = 0
			for (const el of obstacleEls) {
				const r = el.getBoundingClientRect()
				if (r.width > 0 && r.bottom > -60 && r.top < height + 60) {
					obstacles.push({
						left: r.left - HALF,
						right: r.right + HALF,
						top: r.top - HALF,
						bottom: r.bottom + HALF,
					})
				}
			}
			// merge overlapping rects so items can't get wedged into the small
			// gap between two adjacent obstacles (e.g. a title and its subtitle)
			for (let i = 0; i < obstacles.length; i++) {
				for (let j = i + 1; j < obstacles.length; j++) {
					const a = obstacles[i]
					const b = obstacles[j]
					if (
						a.left < b.right &&
						a.right > b.left &&
						a.top < b.bottom &&
						a.bottom > b.top
					) {
						a.left = Math.min(a.left, b.left)
						a.right = Math.max(a.right, b.right)
						a.top = Math.min(a.top, b.top)
						a.bottom = Math.max(a.bottom, b.bottom)
						obstacles.splice(j, 1)
						j = i // re-scan the grown rect against the rest
					}
				}
			}
		}

		const rand = (a: number, b: number) => a + Math.random() * (b - a)

		const populate = () => {
			const target = Math.max(
				MIN_FISH,
				Math.min(MAX_FISH, Math.round((width * height) / AREA_PER_FISH))
			)
			if (fish.length < target) {
				for (let i = fish.length; i < target; i++) {
					fish.push({
						x: rand(0, width),
						y: rand(0, height),
						vx: rand(-0.5, 0.5),
						vy: rand(-0.5, 0.5),
						sprite: Math.floor(Math.random() * SPRITES.length),
						cruise: rand(IDLE_CRUISE_MIN, IDLE_CRUISE_MAX),
					})
				}
			} else {
				fish.length = target
			}
		}

		let sizedWidth = 0
		let sizedHeight = 0
		const resize = () => {
			const rect = canvas.getBoundingClientRect()
			width = Math.max(1, Math.round(rect.width))
			height = Math.max(1, Math.round(rect.height))

			// Mobile browsers show/hide the address bar while scrolling, which
			// fires resize/ResizeObserver with only the viewport HEIGHT shifting
			// by a small amount. Reacting to that like a real resize repopulates
			// (and briefly clears) the canvas, which reads as fish blinking in and
			// out. Only rebuild the backing store + fish list on a real resize:
			// any width change, or a height change bigger than a toolbar's worth.
			const isInitial = sizedWidth === 0 && sizedHeight === 0
			const widthChanged = width !== sizedWidth
			const heightChanged = Math.abs(height - sizedHeight) > 120
			if (!isInitial && !widthChanged && !heightChanged) return

			sizedWidth = width
			sizedHeight = height
			const dpr = Math.min(window.devicePixelRatio || 1, 2)
			canvas.width = width * dpr
			canvas.height = height * dpr
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
			populate()
			// keep fish inside the new bounds
			for (const f of fish) {
				if (f.x > width) f.x = width
				if (f.y > height) f.y = height
			}
		}

		const drawFrame = () => {
			ctx.clearRect(0, 0, width, height)
			ctx.globalAlpha = GLOBAL_ALPHA
			for (let i = 0; i < fish.length; i++) {
				const f = fish[i]
				const img = images[f.sprite]
				if (!img) {
					ctx.beginPath()
					ctx.arc(f.x, f.y, 10, 0, Math.PI * 2)
					ctx.fillStyle = '#60a5fa'
					ctx.fill()
					continue
				}
				ctx.drawImage(img, f.x - HALF, f.y - HALF, FISH_SIZE, FISH_SIZE)
			}
			ctx.globalAlpha = 1
		}

		const step = () => {
			const mx = mouse.x
			const my = mouse.y
			const interact = mouse.inside
			const { top: topBound, bottom: bottomBound } = verticalBounds()
			const topY = topBound + EDGE_MARGIN
			const botY = bottomBound - EDGE_MARGIN
			collectObstacles()

			for (let i = 0; i < fish.length; i++) {
				const f = fish[i]

				// --- pointer interaction ---
				if (interact) {
					const dx = f.x - mx
					const dy = f.y - my
					const d2 = dx * dx + dy * dy
					if (mouse.down) {
						// pull into a vortex toward the cursor
						if (d2 > 25 && d2 < ATTRACT_RADIUS * ATTRACT_RADIUS) {
							const d = Math.sqrt(d2)
							const t = 1 - d / 1000
							const mag = t * t * ATTRACT_FORCE * SPEED
							const inv = 1 / d
							const along = mag * inv
							const perp = mag * ATTRACT_SWIRL * inv
							f.vx -= dx * along - dy * perp
							f.vy -= dy * along + dx * perp
						}
					} else if (d2 < REPEL_RADIUS * REPEL_RADIUS && d2 > 0.01) {
						// scatter away from the cursor
						const d = Math.sqrt(d2)
						const t = 1 - d / REPEL_RADIUS
						const mag = t * t * REPEL_FORCE * SPEED
						const inv = 1 / d
						const along = mag * inv
						const perp = mag * REPEL_SWIRL * inv
						f.vx += dx * along - dy * perp
						f.vy += dy * along + dx * perp
					}
				}

				// --- separation from neighbours (small, capped count -> O(n²) is fine) ---
				for (let j = 0; j < fish.length; j++) {
					if (j === i) continue
					const g = fish[j]
					const dx = f.x - g.x
					const dy = f.y - g.y
					const d2 = dx * dx + dy * dy
					if (d2 < SEPARATION_RADIUS * SEPARATION_RADIUS && d2 > 1) {
						const d = Math.sqrt(d2)
						const s =
							((SEPARATION_RADIUS - d) / SEPARATION_RADIUS) *
							SEPARATION_FORCE *
							SPEED
						const inv = 1 / d
						f.vx += dx * inv * s
						f.vy += dy * inv * s
					}
				}

				// --- idle wander ---
				f.vx += (Math.random() - 0.5) * WANDER
				f.vy += (Math.random() - 0.5) * WANDER

				// --- speed clamp ---
				const sp2 = f.vx * f.vx + f.vy * f.vy
				if (sp2 > MAX_SPEED * MAX_SPEED) {
					const k = MAX_SPEED / Math.sqrt(sp2)
					f.vx *= k
					f.vy *= k
				}

				// --- bounce off edges ---
				if (f.x < EDGE_MARGIN) {
					f.x = EDGE_MARGIN
					f.vx = Math.abs(f.vx)
				} else if (f.x > width - EDGE_MARGIN) {
					f.x = width - EDGE_MARGIN
					f.vx = -Math.abs(f.vx)
				}
				if (f.y < topY) {
					f.y = topY
					f.vy = Math.abs(f.vy)
				} else if (f.y > botY) {
					f.y = botY
					f.vy = -Math.abs(f.vy)
				}

				// --- friction ---
				f.vx *= FRICTION
				f.vy *= FRICTION

				// --- keep a lively idle cruise so fish never stall when left alone ---
				const sp = Math.sqrt(f.vx * f.vx + f.vy * f.vy)
				if (sp < f.cruise) {
					if (sp > 0.0001) {
						const k = f.cruise / sp
						f.vx *= k
						f.vy *= k
					} else {
						const a = Math.random() * Math.PI * 2
						f.vx = Math.cos(a) * f.cruise
						f.vy = Math.sin(a) * f.cruise
					}
				}

				// --- integrate ---
				f.x += f.vx
				f.y += f.vy

				// --- bounce off solid cards (impassable obstacles) ---
				for (let o = 0; o < obstacles.length; o++) {
					const r = obstacles[o]
					if (f.x > r.left && f.x < r.right && f.y > r.top && f.y < r.bottom) {
						const dl = f.x - r.left
						const dr = r.right - f.x
						const dt = f.y - r.top
						const db = r.bottom - f.y
						const m = Math.min(dl, dr, dt, db)
						if (m === dl) {
							f.x = r.left
							f.vx = -Math.abs(f.vx)
						} else if (m === dr) {
							f.x = r.right
							f.vx = Math.abs(f.vx)
						} else if (m === dt) {
							f.y = r.top
							f.vy = -Math.abs(f.vy)
						} else {
							f.y = r.bottom
							f.vy = Math.abs(f.vy)
						}
					}
				}

			}
		}

		// --- reduced motion: one static frame, no loop / listeners ---
		if (reduceMotion) {
			const sb = verticalBounds()
			for (const f of fish) {
				if (f.y < sb.top + EDGE_MARGIN) f.y = sb.top + EDGE_MARGIN
				else if (f.y > sb.bottom - EDGE_MARGIN) f.y = sb.bottom - EDGE_MARGIN
			}
			let tries = 0
			const renderOnce = () => {
				drawFrame()
				if (loaded < SPRITES.length && tries < 40) {
					tries++
					setTimeout(renderOnce, 100)
				}
			}
			renderOnce()
			const ro = new ResizeObserver(() => {
				resize()
				drawFrame()
			})
			ro.observe(canvas)
			return () => ro.disconnect()
		}

		// --- animation loop ---
		let raf = 0
		let running = false
		const loop = () => {
			step()
			drawFrame()
			raf = requestAnimationFrame(loop)
		}
		const start = () => {
			if (!running) {
				running = true
				raf = requestAnimationFrame(loop)
			}
		}
		const stop = () => {
			if (running) {
				running = false
				cancelAnimationFrame(raf)
			}
		}

		// --- pointer (canvas is pointer-events-none, so listen on window) ---
		const onMove = (e: MouseEvent) => {
			const rect = canvas.getBoundingClientRect()
			const x = e.clientX - rect.left
			const y = e.clientY - rect.top
			mouse.x = x
			mouse.y = y
			mouse.inside =
				x >= -REPEL_RADIUS &&
				x <= width + REPEL_RADIUS &&
				y >= -REPEL_RADIUS &&
				y <= height + REPEL_RADIUS
		}
		const onDown = () => {
			mouse.down = true
		}
		const onUp = () => {
			mouse.down = false
		}
		const onLeave = () => {
			mouse.inside = false
			mouse.down = false
		}

		window.addEventListener('mousemove', onMove, { passive: true })
		window.addEventListener('mousedown', onDown, { passive: true })
		window.addEventListener('mouseup', onUp, { passive: true })
		window.addEventListener('blur', onUp)
		document.addEventListener('mouseleave', onLeave)

		resize()
		const ro = new ResizeObserver(() => resize())
		ro.observe(canvas)

		// pause the loop while the hero is off-screen
		const io = new IntersectionObserver(
			entries => {
				if (entries[0]?.isIntersecting) start()
				else stop()
			},
			{ threshold: 0 }
		)
		io.observe(canvas)

		return () => {
			stop()
			ro.disconnect()
			io.disconnect()
			window.removeEventListener('mousemove', onMove)
			window.removeEventListener('mousedown', onDown)
			window.removeEventListener('mouseup', onUp)
			window.removeEventListener('blur', onUp)
			document.removeEventListener('mouseleave', onLeave)
		}
	}, [])

	return (
		<canvas
			ref={canvasRef}
			aria-hidden='true'
			className={`pointer-events-none fixed inset-0 z-0 h-full w-full ${className}`}
		/>
	)
}
